// ─── Retouches (essai) — the album edit agent, phase 1 ──────────────────────
//
// Owner, 2026-10-31: « I copy his request, tell you to do it, and you do it
// correctly — I want an agent that does that automatically. » Phase 1 is a
// SHADOW: for every client edit request, Claude plans it too on a copy of the
// album and looks at its own result (worker lib/albumEditShadow.ts). Nothing
// here reaches the album or the client. Each card shows her words, what really
// happened (Qwen), what Claude would have done, the pages it changed (before →
// after), and three buttons: Juste / En partie / Faux. Those verdicts decide
// when phase 2 (Claude live behind one tap) may start.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type EditShadowItem, type EditShadowPlan, type EditShadowChecked } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Card, CardHeader, EmptyState } from './_ui';

const KEY = 'edit-shadows';

/** Her part of the manifest: the preview notes and the chat, not the page list. */
export function clientPart(manifest: string | null): string {
  const m = String(manifest ?? '');
  const i = ['NOTES THE CLIENT LEFT', 'WHAT THE CLIENT SENT AND SAID', 'WHAT THE OWNER ASKED']
    .map((h) => m.indexOf(h)).filter((x) => x >= 0).sort((a, b) => a - b)[0];
  return (i === undefined ? m : m.slice(i)).trim();
}

function Steps({ plan, checked }: { plan: EditShadowPlan | null; checked: EditShadowChecked | null }) {
  const { t } = useT();
  if (!plan) return <p className="text-[12px] text-gray-400">{t('Pas de plan')}</p>;
  const steps = checked?.valid ?? [];
  return (
    <div className="space-y-1.5">
      <p className="text-[12px] text-gray-700 leading-snug">{plan.understanding}</p>
      <ol className="list-decimal pl-4 text-[12px] text-gray-800 space-y-0.5">
        {steps.map((s) => <li key={s.index}>{s.summary}{s.hold ? ` · ${t('attend le patron')}` : ''}</li>)}
        {!steps.length && <li className="list-none -ml-4 text-gray-400">{t('Aucune modification')}</li>}
      </ol>
      {[...plan.cannot.map((c) => `« ${c.clientWords} » — ${c.why}`), ...plan.needsOwner.map((c) => `${c.topic} : « ${c.clientWords} »`)].map((x, i) => (
        <p key={i} className="text-[11px] text-amber-700 leading-snug">{t('Pour le patron')} : {x}</p>
      ))}
    </div>
  );
}

function Thumbs({ ids, thumbs, text }: { ids: string[] | null; thumbs: Record<string, string>; text?: string | null }) {
  const { t } = useT();
  if (!ids) return <span className="text-[11px] text-gray-400">{t('pas de page')}</span>;
  // A page with words and no photo is a TEXT page, not an empty one (#WA-764431).
  if (!ids.length && text) return <span className="text-[11px] text-gray-700 italic max-w-[16rem] truncate" title={text}>{t('texte')} : « {text} »</span>;
  // Older rows did not record the words: say what is known, not « vide ».
  if (!ids.length) return <span className={`text-[11px] ${text === undefined ? 'text-gray-500' : 'text-red-600'}`}>{text === undefined ? t('sans photo') : t('page vide')}</span>;
  return (
    <span className="inline-flex gap-1">
      {ids.map((id, i) => (id.startsWith('new:') || !thumbs[id]
        ? <span key={i} className="w-10 h-10 rounded bg-gray-100 text-[10px] text-gray-500 flex items-center justify-center">{id.startsWith('new:') ? id.slice(4) : '?'}</span>
        : <img key={i} src={thumbs[id]} alt="" className="w-10 h-10 rounded object-cover" loading="lazy" />))}
    </span>
  );
}

function ShadowCard({ s, thumbs, onOpenChat }: { s: EditShadowItem; thumbs: Record<string, string>; onOpenChat?: (chatId: string) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [note, setNote] = useState(s.owner_note ?? '');
  const verdict = useMutation({
    mutationFn: (v: 'right' | 'wrong' | 'partly') => whatsappApi.editShadowVerdict(s.id, v, note.trim() || undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
  const rounds = s.rounds?.length ?? 0;
  const cost = s.cost_usd != null ? `$${Number(s.cost_usd).toFixed(2)}` : '—';
  const secs = s.duration_ms != null ? `${Math.round(s.duration_ms / 1000)} s` : '—';
  const choices: Array<['right' | 'partly' | 'wrong', string]> = [['right', t('Juste')], ['partly', t('En partie')], ['wrong', t('Faux')]];

  return (
    <Card pad={false}>
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[13px] font-semibold text-gray-800">{s.order_name ?? '—'}{s.customer_name ? ` · ${s.customer_name}` : ''}</span>
        <span className="text-[11px] text-gray-500">{new Date(s.created_at).toLocaleString()}</span>
        <span className="text-[11px] text-gray-500">{s.source === 'replay' ? t('rejoué') : t('en direct')}</span>
        <span className={`text-[11px] ${s.self_ok ? 'text-emerald-700' : 'text-amber-700'}`}>
          {s.status === 'failed' ? t('échec') : s.self_ok ? t('vérifié par Claude') : t('Claude a vu un problème')}
        </span>
        <span className="text-[11px] text-gray-400 ml-auto">{rounds} {t('essai(s)')} · {cost} · {secs}</span>
        {s.chat_id && onOpenChat && (
          <button type="button" onClick={() => onOpenChat(s.chat_id as string)} className="text-[11px] text-blue-700 hover:underline">{t('Ouvrir la conversation')}</button>
        )}
      </div>

      <details className="px-4 py-2 border-b border-gray-100">
        <summary className="text-[12px] text-gray-600 cursor-pointer">{t('Ce que le client a demandé')}</summary>
        <pre className="mt-2 text-[11px] text-gray-700 whitespace-pre-wrap leading-snug max-h-64 overflow-auto">{clientPart(s.client_words)}</pre>
      </details>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:divide-x divide-gray-100">
        <div className="px-4 py-3">
          <h4 className="text-[11px] font-semibold text-gray-500 mb-1.5">{t('Ce qui a vraiment été fait (Qwen)')}</h4>
          <Steps plan={s.qwen_plan} checked={s.qwen_checked} />
        </div>
        <div className="px-4 py-3">
          <h4 className="text-[11px] font-semibold text-gray-500 mb-1.5">{t('Ce que Claude aurait fait')}</h4>
          {s.status === 'failed' ? <p className="text-[12px] text-red-600">{s.error}</p> : <Steps plan={s.claude_plan} checked={s.claude_checked} />}
        </div>
      </div>

      {(s.problems?.length ?? 0) > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 space-y-0.5">
          {s.problems.map((p, i) => <p key={i} className="text-[11px] text-amber-800">⚠ {p}</p>)}
        </div>
      )}

      {(s.changed?.length ?? 0) > 0 && (
        <div className="px-4 py-3 border-t border-gray-100">
          <h4 className="text-[11px] font-semibold text-gray-500 mb-2">{t('Pages changées par Claude (avant → après)')}</h4>
          <div className="space-y-1.5">
            {(s.changed ?? []).slice(0, 30).map((c) => (
              <div key={c.at} className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-gray-500 w-12 shrink-0">p{c.at}</span>
                <Thumbs ids={c.before} thumbs={thumbs} text={c.beforeText} />
                <span className="text-gray-400 text-[12px]" aria-hidden="true">→</span>
                <Thumbs ids={c.after} thumbs={thumbs} text={c.afterText} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={t('Ce qui manque ou ce qui est faux (facultatif)')}
          className="flex-1 min-w-[12rem] text-[12px] border border-gray-200 rounded-md px-2 py-1.5"
        />
        {choices.map(([v, label]) => (
          <button
            key={v} type="button" disabled={verdict.isPending} onClick={() => verdict.mutate(v)}
            className={`text-[12px] px-3 py-1.5 rounded-md border ${s.owner_verdict === v
              ? (v === 'right' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : v === 'wrong' ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800')
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >{label}</button>
        ))}
      </div>
    </Card>
  );
}

export function EditShadowTab({ onOpenChat }: { onOpenChat?: (chatId: string) => void }) {
  const { t } = useT();
  const q = useQuery({ queryKey: [KEY], queryFn: () => whatsappApi.editShadows(30), refetchInterval: 120_000 });
  const items = q.data?.items ?? [];
  const judged = items.filter((s) => s.owner_verdict);
  const right = judged.filter((s) => s.owner_verdict === 'right').length;
  return (
    <div className="space-y-4">
      <Card pad={false}>
        <CardHeader
          title={t('Retouches d’album — essai de Claude')}
          sub={t('Pour chaque demande de retouche, Claude fait le travail sur une copie de l’album et vérifie son résultat. Rien n’est envoyé au client et l’album ne change pas. Dites si c’est juste : quand il a raison presque à chaque fois, il pourra faire les retouches lui-même après votre OK.')}
          right={judged.length ? `${right}/${judged.length} ${t('justes')}` : String(items.length)}
        />
      </Card>
      {q.isLoading && <p className="text-[12px] text-gray-400">{t('Chargement…')}</p>}
      {!q.isLoading && !items.length && <Card><EmptyState>{t('Pas encore d’essai. Le prochain message de retouche d’un client apparaîtra ici.')}</EmptyState></Card>}
      {items.map((s) => <ShadowCard key={s.id} s={s} thumbs={q.data?.thumbs ?? {}} onOpenChat={onOpenChat} />)}
    </div>
  );
}
